import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Printer, Download, RefreshCw, Package, Archive, CheckCircle2, Eye,
  Search, ShieldCheck, Clock, FileText, FileArchive,
} from "lucide-react";
import { StatusBadge, ShipmentDetail } from "./UploadPanel";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const PrintPanel = ({ user }) => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusLabels, setStatusLabels] = useState({});
  const [detailShipment, setDetailShipment] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sh, cat] = await Promise.all([
        axios.get(`${API}/shipments?scope=shared`, { headers: getAuthHeader() }),
        axios.get(`${API}/shipments/status-catalog`, { headers: getAuthHeader() }),
      ]);
      setShipments(sh.data.shipments || []);
      setStatusLabels(cat.data.labels || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/shipments/${id}`, { headers: getAuthHeader() });
      setDetailShipment(r.data);
    } catch (e) { toast.error("Failed to load shipment"); }
  };

  const markPrinted = async (id) => {
    try {
      await axios.post(`${API}/shipments/${id}/mark-printed`, {}, { headers: getAuthHeader() });
      toast.success("Marked as printed — Back Office notified");
      await load();
      if (detailShipment?.id === id) {
        const r = await axios.get(`${API}/shipments/${id}`, { headers: getAuthHeader() });
        setDetailShipment(r.data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const archiveShipment = async (id) => {
    if (!window.confirm("Move this shipment to archive?")) return;
    try {
      await axios.post(`${API}/shipments/${id}/archive`, {}, { headers: getAuthHeader() });
      toast.success("Archived");
      setDetailShipment(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const printAll = async (shipment) => {
    // Fetch each doc as blob and open a new tab per doc for the user to print via browser
    for (const d of (shipment.generated_docs || [])) {
      try {
        const r = await axios.get(`${API}/shipments/${shipment.id}/documents/${d.id}/download`,
                                  { headers: getAuthHeader(), responseType: "blob" });
        const url = URL.createObjectURL(new Blob([r.data]));
        window.open(url, "_blank");
      } catch { /* skip one */ }
    }
    await markPrinted(shipment.id);
  };

  const downloadBlob = async (path, defaultName) => {
    try {
      const r = await axios.get(`${API}/shipments/${path}`,
                                { headers: getAuthHeader(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      // Try to read filename from headers
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : defaultName;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const downloadZip = (shipment) =>
    downloadBlob(`${shipment.id}/documents.zip`, `${shipment.shipment_no}_documents.zip`);

  const downloadCombinedPdf = (shipment) =>
    downloadBlob(`${shipment.id}/documents.pdf`, `${shipment.shipment_no}_combined.pdf`);

  const filtered = shipments.filter(s => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (s.shipment_no || "").toLowerCase().includes(q) ||
      (s.company_name || "").toLowerCase().includes(q) ||
      (s.customer_name || "").toLowerCase().includes(q) ||
      (s.invoice_no || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-4 md:p-6 space-y-4 min-h-full bg-slate-50" data-testid="print-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Printer className="w-7 h-7 text-teal-600" />
            Print Panel — Documents Ready to Print
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Shipments shared by the Back Office. Print individual files or all documents in one click.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)}
                   className="pl-8 w-56 h-9 text-sm" data-testid="print-search" />
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="print-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <Card className="bg-white/80 border-0 shadow-sm">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="print-empty">
              {loading ? "Loading…" : "No documents shared yet. When Back Office clicks 'OK & Share', shipments appear here."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-3 py-2">Shipment #</th>
                    <th className="text-left px-3 py-2">Company</th>
                    <th className="text-left px-3 py-2">Customer</th>
                    <th className="text-left px-3 py-2">Invoice</th>
                    <th className="text-left px-3 py-2">Docs</th>
                    <th className="text-left px-3 py-2">Shared</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(s => (
                    <tr key={s.id} className="border-b hover:bg-slate-50" data-testid={`print-row-${s.id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{s.shipment_no}</td>
                      <td className="px-3 py-2 text-xs">{s.company_name || "—"}</td>
                      <td className="px-3 py-2 text-xs">{s.customer_name || "—"}</td>
                      <td className="px-3 py-2 text-xs">{s.invoice_no || "—"}</td>
                      <td className="px-3 py-2 text-xs">
                        <Badge className="bg-violet-100 text-violet-700 text-[10px]">
                          {(s.generated_docs || []).length} file(s)
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {fmtDate(s.shared_at)}<br /><span className="text-[10px]">by {s.shared_by}</span>
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={s.status} label={statusLabels[s.status] || s.status} />
                      </td>
                      <td className="px-3 py-2 text-right space-x-1">
                        <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                                onClick={() => openDetail(s.id)}
                                data-testid={`print-detail-${s.id}`}>
                          <Eye className="w-3 h-3 mr-1" /> View
                        </Button>
                        <Button size="sm" variant="outline"
                                className="h-7 px-2 text-[11px] text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                                onClick={() => downloadZip(s)}
                                data-testid={`print-zip-${s.id}`}
                                disabled={!(s.generated_docs || []).length}
                                title="Download all documents as ZIP">
                          <FileArchive className="w-3 h-3 mr-1" /> ZIP
                        </Button>
                        <Button size="sm" variant="outline"
                                className="h-7 px-2 text-[11px] text-red-700 border-red-200 hover:bg-red-50"
                                onClick={() => downloadCombinedPdf(s)}
                                data-testid={`print-pdf-${s.id}`}
                                disabled={!(s.generated_docs || []).length}
                                title="Download combined PDF (PDFs only)">
                          <FileText className="w-3 h-3 mr-1" /> PDF
                        </Button>
                        <Button size="sm"
                                className="h-7 px-2 text-[11px] bg-teal-600 hover:bg-teal-700 text-white"
                                onClick={() => printAll(s)}
                                data-testid={`print-all-${s.id}`}
                                disabled={!(s.generated_docs || []).length}>
                          <Printer className="w-3 h-3 mr-1" /> Print All
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-slate-500"
                                onClick={() => archiveShipment(s.id)}
                                data-testid={`print-archive-${s.id}`}>
                          <Archive className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <ShipmentDetail
        shipment={detailShipment}
        onClose={() => setDetailShipment(null)}
        statusLabels={statusLabels}
        actions={
          detailShipment && (
            <div className="flex gap-2 mr-auto">
              <Button size="sm"
                      className="bg-teal-600 hover:bg-teal-700 text-white"
                      onClick={() => printAll(detailShipment)}
                      disabled={!(detailShipment.generated_docs || []).length}
                      data-testid="detail-print-all">
                <Printer className="w-4 h-4 mr-1" /> Print All & Mark Done
              </Button>
              {detailShipment.status !== "archived" && (
                <Button size="sm" variant="outline"
                        onClick={() => archiveShipment(detailShipment.id)}
                        data-testid="detail-archive">
                  <Archive className="w-4 h-4 mr-1" /> Archive
                </Button>
              )}
            </div>
          )
        }
      />
    </div>
  );
};

export default PrintPanel;
